import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { ImportProposal } from "./models/ImportProposal";
import { ImportMember } from "./models/ImportMember";
import { ImportGroup } from "./models/ImportGroup";
import { ImportSession } from "./models/ImportSession";
import { ImportResult } from "./models/ImportResult";
import { confirmImport, extractImportProposal } from "./slice-extensions/assistant-import-slice-extension";
import { extractionProgressed } from "./slice-extensions/assistant-import-actions";
import { ImportExtractionStatus } from "./contracts/ImportExtractionStatus";

export interface AssistantImportState {
  proposal: ImportProposal | null;
  result: ImportResult | null;
  extractStatus: LoadingStatus;
  /** Status of the running background extraction; null while uploading or when none runs. */
  extractionStatus: ImportExtractionStatus | null;
  confirmStatus: LoadingStatus;
  statusMessage: string | undefined;
}

const initialState: AssistantImportState = {
  proposal: null,
  result: null,
  extractStatus: LoadingStatus.Idle,
  extractionStatus: null,
  confirmStatus: LoadingStatus.Idle,
  statusMessage: undefined,
};

const assistantImportSlice = createSlice({
  name: "assistantImport",
  initialState,
  reducers: {
    updateMember: (
      state,
      action: PayloadAction<{ tempId: string; changes: Partial<ImportMember> }>,
    ) => {
      const member = state.proposal?.members.find((m) => m.tempId === action.payload.tempId);
      if (member) {
        Object.assign(member, action.payload.changes);
      }
    },
    removeMember: (state, action: PayloadAction<string>) => {
      if (!state.proposal) return;
      const tempId = action.payload;
      state.proposal.members = state.proposal.members.filter((m) => m.tempId !== tempId);
      state.proposal.groups.forEach((group) => {
        group.memberTempIds = group.memberTempIds.filter((id) => id !== tempId);
      });
      state.proposal.sessions.forEach((session) => {
        session.memberTempIds = session.memberTempIds.filter((id) => id !== tempId);
      });
    },
    updateGroup: (
      state,
      action: PayloadAction<{ tempId: string; changes: Partial<ImportGroup> }>,
    ) => {
      const group = state.proposal?.groups.find((g) => g.tempId === action.payload.tempId);
      if (group) {
        Object.assign(group, action.payload.changes);
      }
    },
    removeGroup: (state, action: PayloadAction<string>) => {
      if (!state.proposal) return;
      const tempId = action.payload;
      state.proposal.groups = state.proposal.groups.filter((g) => g.tempId !== tempId);
      state.proposal.sessions.forEach((session) => {
        session.groupTempIds = session.groupTempIds.filter((id) => id !== tempId);
      });
    },
    updateSession: (
      state,
      action: PayloadAction<{ tempId: string; changes: Partial<ImportSession> }>,
    ) => {
      const session = state.proposal?.sessions.find((s) => s.tempId === action.payload.tempId);
      if (session) {
        Object.assign(session, action.payload.changes);
      }
    },
    removeSession: (state, action: PayloadAction<string>) => {
      if (!state.proposal) return;
      state.proposal.sessions = state.proposal.sessions.filter(
        (s) => s.tempId !== action.payload,
      );
    },
    resetImport: () => initialState,
    clearError: (state) => {
      state.statusMessage = undefined;
      if (state.extractStatus === LoadingStatus.Failed) {
        state.extractStatus = LoadingStatus.Idle;
      }
      if (state.confirmStatus === LoadingStatus.Failed) {
        state.confirmStatus = LoadingStatus.Idle;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(extractionProgressed, (state, action) => {
        state.extractionStatus = action.payload;
      })
      .addCase(extractImportProposal.pending, (state) => {
        state.extractStatus = LoadingStatus.Loading;
        state.extractionStatus = null;
        state.statusMessage = undefined;
      })
      .addCase(extractImportProposal.fulfilled, (state, action) => {
        state.extractStatus = LoadingStatus.Idle;
        state.extractionStatus = null;
        state.proposal = action.payload;
        state.result = null;
      })
      .addCase(extractImportProposal.rejected, (state, action) => {
        state.extractionStatus = null;
        if (action.meta.aborted) {
          state.extractStatus = LoadingStatus.Idle;
          return;
        }
        state.extractStatus = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to analyze the material";
      })
      .addCase(confirmImport.pending, (state) => {
        state.confirmStatus = LoadingStatus.Loading;
        state.statusMessage = undefined;
      })
      .addCase(confirmImport.fulfilled, (state, action) => {
        state.confirmStatus = LoadingStatus.Idle;
        state.result = action.payload;
      })
      .addCase(confirmImport.rejected, (state, action) => {
        state.confirmStatus = LoadingStatus.Failed;
        const error = action.payload as ApiErrorResponse;
        state.statusMessage = error?.message || "Failed to create the imported items";
      });
  },
});

export const { actions, reducer } = assistantImportSlice;
export const {
  updateMember,
  removeMember,
  updateGroup,
  removeGroup,
  updateSession,
  removeSession,
  resetImport,
  clearError,
} = actions;

export const selectImportProposal = (state: { assistantImport: AssistantImportState }) =>
  state.assistantImport.proposal;
export const selectImportResult = (state: { assistantImport: AssistantImportState }) =>
  state.assistantImport.result;
export const selectExtractStatus = (state: { assistantImport: AssistantImportState }) =>
  state.assistantImport.extractStatus;
export const selectExtractionStatus = (state: { assistantImport: AssistantImportState }) =>
  state.assistantImport.extractionStatus;
export const selectConfirmStatus = (state: { assistantImport: AssistantImportState }) =>
  state.assistantImport.confirmStatus;
export const selectImportStatusMessage = (state: { assistantImport: AssistantImportState }) =>
  state.assistantImport.statusMessage;

export default reducer;
